"use strict";
/**
 * Wrapper do ładowania szablonu Excel z API
 * Zawiera logikę transformacji danych z API do formatu Excel
 * Zbudowany jako zestaw niezależnych funkcji
 *
 * ═══════════════════════════════════════════════════════════
 * JEDNOSTKI WYMIARÓW
 * ═══════════════════════════════════════════════════════════
 *
 * API BACKEND (C# z NPOI):
 * - columnWidths: PIKSELE (dla DPI 96)
 * - rowHeights: PIKSELE (dla DPI 96)
 *
 * EXCEL API (JavaScript):
 * - columnWidth: PUNKTY (points)
 * - rowHeight: PUNKTY (points)
 *
 * KONWERSJA:
 * - 1 piksel @ 96 DPI = 0.75 punktu
 * - Piksele → Punkty: pixels * 0.75
 * - Punkty → Piksele: points * 1.333...
 *
 * Ten moduł automatycznie konwertuje wartości z pikseli (API)
 * na punkty (Excel API) podczas aplikowania wymiarów.
 * ═══════════════════════════════════════════════════════════
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSheetFromData = createSheetFromData;
/**
 * Wykrywa kompatybilność platformy i wersji Excel
 */
function detectPlatformCompatibility() {
    var _a;
    var version = ((_a = Office.context.diagnostics) === null || _a === void 0 ? void 0 : _a.version) || 'unknown';
    var platform = Office.context.platform || 'unknown';
    var host = Office.context.host || 'unknown';
    // Wykryj Windows 10 na podstawie version string
    // Excel Desktop na Windows 10 często ma wersje 16.0.x ale z ograniczeniami API
    var isWindows10 = platform === Office.PlatformType.PC &&
        (version.includes('16.0') || version.includes('16.01'));
    // Wykryj czy Rich Text API jest dostępne
    // Na Windows 10 Excel Desktop niektóre wersje mają problemy z Rich Text
    var supportsRichText = !isWindows10 &&
        platform !== Office.PlatformType.OfficeOnline;
    // Wykryj czy Screen Updating API jest dostępne
    // Na Windows 10 Excel Desktop suspendScreenUpdatingUntilNextSync może powodować błędy
    var supportsScreenUpdating = !isWindows10;
    // Określ optymalny rozmiar batch dla operacji
    var maxCellsPerBatch = isWindows10 ? 25 : 50;
    return {
        supportsRichText: supportsRichText,
        supportsBatchOperations: true,
        supportsScreenUpdating: supportsScreenUpdating,
        maxCellsPerBatch: maxCellsPerBatch,
        platform: platform.toString(),
        version: version,
        isWindows10: isWindows10
    };
}
/**
 * Bezpieczny wrapper dla suspendScreenUpdatingUntilNextSync()
 * Nie wszystkie wersje Excel obsługują tę funkcję (szczególnie Windows 10)
 */
function safelySuspendScreenUpdating(context, compatibility) {
    try {
        // Sprawdź kompatybilność na podstawie wykrytej platformy
        if (!compatibility.supportsScreenUpdating) {
            console.log("\uD83D\uDFE1 ".concat(compatibility.isWindows10 ? 'Windows 10' : 'Ta platforma', ": pomijam suspendScreenUpdatingUntilNextSync() ze wzgl\u0119d\u00F3w kompatybilno\u015Bci"));
            return false;
        }
        // Sprawdź czy funkcja istnieje
        if (context.application && typeof context.application.suspendScreenUpdatingUntilNextSync === 'function') {
            context.application.suspendScreenUpdatingUntilNextSync();
            console.log("\u23F8\uFE0F Screen updating suspended");
            return true;
        }
        else {
            console.log("\u26A0\uFE0F suspendScreenUpdatingUntilNextSync() niedost\u0119pne na tej platformie");
            return false;
        }
    }
    catch (error) {
        console.warn("\u26A0\uFE0F B\u0142\u0105d przy suspendScreenUpdating:", error);
        return false;
    }
}
/**
 * Tworzy nowy arkusz Excel z danych pobranych z API
 * Jeśli arkusz o podanej nazwie już istnieje, zostanie usunięty i utworzony od nowa
 */
function createSheetFromData(sheetData) {
    return __awaiter(this, void 0, void 0, function () {
        var _this = this;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Excel.run(function (context) { return __awaiter(_this, void 0, void 0, function () {
                        var compatibility, screenUpdateSuspended, existingSheet, newSheet, cellsError_1, error_1;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    _a.trys.push([0, 16, , 17]);
                                    compatibility = detectPlatformCompatibility();
                                    console.log("\uD83D\uDD27 G\u0142\u00F3wna funkcja - wykryto kompatybilno\u015B\u0107:", compatibility);
                                    screenUpdateSuspended = safelySuspendScreenUpdating(context, compatibility);
                                    existingSheet = context.workbook.worksheets.getItemOrNullObject(sheetData.sheetName);
                                    existingSheet.load('name');
                                    return [4 /*yield*/, context.sync()];
                                case 1:
                                    _a.sent();
                                    if (!!existingSheet.isNullObject) return [3 /*break*/, 3];
                                    console.log("\uD83D\uDDD1\uFE0F Usuwanie istniej\u0105cego arkusza: \"".concat(sheetData.sheetName, "\""));
                                    existingSheet.delete();
                                    return [4 /*yield*/, context.sync()];
                                case 2:
                                    _a.sent();
                                    _a.label = 3;
                                case 3:
                                    newSheet = context.workbook.worksheets.add(sheetData.sheetName);
                                    // Aktywuj nowy arkusz
                                    newSheet.activate();
                                    console.log("\uD83D\uDCC4 Utworzono nowy arkusz: \"".concat(sheetData.sheetName, "\""));
                                    console.log("\uD83D\uDCCA Zakres danych: ".concat(sheetData.usedRange));
                                    console.log("\uD83D\uDCCF Wymiary: ".concat(sheetData.rowCount, " wierszy x ").concat(sheetData.columnCount, " kolumn"));
                                    // 2. Ukryj linie siatki
                                    if (sheetData.showGridLines === false) {
                                        newSheet.showGridlines = false;
                                        console.log('🔲 Ukryto linie siatki');
                                    }
                                    // 3. STRUKTURA - wymiary i kolory tła
                                    return [4 /*yield*/, applyStructure(context, newSheet, sheetData.structure)];
                                case 4:
                                    // 3. STRUKTURA - wymiary i kolory tła
                                    _a.sent();
                                    if (!compatibility.isWindows10) return [3 /*break*/, 10];
                                    console.log("\uD83D\uDFE1 Windows 10 wykryte - u\u017Cywam ostro\u017Cnej strategii \u0142adowania");
                                    _a.label = 5;
                                case 5:
                                    _a.trys.push([5, 7, , 9]);
                                    return [4 /*yield*/, applyCells(context, newSheet, sheetData.cells)];
                                case 6:
                                    _a.sent();
                                    return [3 /*break*/, 9];
                                case 7:
                                    cellsError_1 = _a.sent();
                                    console.error("\u274C B\u0142\u0105d aplikowania kom\u00F3rek na Windows 10:", cellsError_1);
                                    // Windows 10 fallback: aplikuj komórki w jeszcze mniejszych batch
                                    console.log("\uD83D\uDD04 Fallback: pr\u00F3ba aplikowania kom\u00F3rek w batch x10");
                                    return [4 /*yield*/, applyCellsWindows10Fallback(context, newSheet, sheetData.cells)];
                                case 8:
                                    _a.sent();
                                    return [3 /*break*/, 9];
                                case 9: return [3 /*break*/, 12];
                                case 10: return [4 /*yield*/, applyCells(context, newSheet, sheetData.cells)];
                                case 11:
                                    _a.sent();
                                    _a.label = 12;
                                case 12: 
                                // 5. SCALONE KOMÓRKI
                                return [4 /*yield*/, applyMergedCells(context, newSheet, sheetData.mergedCells)];
                                case 13:
                                    // 5. SCALONE KOMÓRKI
                                    _a.sent();
                                    return [4 /*yield*/, context.sync()];
                                case 14:
                                    _a.sent();
                                    console.log('✅ Arkusz gotowy');
                                    return [4 /*yield*/, context.sync()];
                                case 15:
                                    _a.sent();
                                    console.log("\u2705 Arkusz \"".concat(sheetData.sheetName, "\" zosta\u0142 pomy\u015Blnie utworzony"));
                                    return [3 /*break*/, 17];
                                case 16:
                                    error_1 = _a.sent();
                                    console.error('❌ Error creating sheet:', error_1);
                                    throw error_1;
                                case 17: return [2 /*return*/];
                            }
                        });
                    }); })];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * ═══════════════════════════════════════════════════════════
 * KROK 3: APLIKACJA STRUKTURY
 * ═══════════════════════════════════════════════════════════
 */
/**
 * Konwertuje indeks kolumny (0-based) na literę kolumny Excel (A, B, C, ..., Z, AA, AB, ...)
 */
function getColumnLetter(columnIndex) {
    var letter = '';
    var index = columnIndex;
    while (index >= 0) {
        letter = String.fromCharCode((index % 26) + 65) + letter;
        index = Math.floor(index / 26) - 1;
    }
    return letter;
}
/**
 * Aplikuje strukturę arkusza - wymiary kolumn, wierszy i kolory tła
 */
function applyStructure(context, sheet, structure) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    console.log('📐 Aplikowanie struktury arkusza...');
                    // 3a. Szerokości kolumn
                    return [4 /*yield*/, applyColumnWidths(context, sheet, structure.columnWidths)];
                case 1:
                    // 3a. Szerokości kolumn
                    _a.sent();
                    // 3b. Wysokości wierszy
                    return [4 /*yield*/, applyRowHeights(context, sheet, structure.rowHeights)];
                case 2:
                    // 3b. Wysokości wierszy
                    _a.sent();
                    if (!(structure.rowBackgroundColors && Object.keys(structure.rowBackgroundColors).length > 0)) return [3 /*break*/, 4];
                    return [4 /*yield*/, applyRowBackgroundColors(context, sheet, structure.rowBackgroundColors)];
                case 3:
                    _a.sent();
                    _a.label = 4;
                case 4: return [4 /*yield*/, context.sync()];
                case 5:
                    _a.sent();
                    console.log('✅ Struktura arkusza zastosowana');
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Ustawia szerokości kolumn
 * API zwraca szerokości w PIKSELACH (dla DPI 96)
 * Excel API wymaga jednostek PUNKTÓW (points)
 */
function applyColumnWidths(context, sheet, columnWidths) {
    return __awaiter(this, void 0, void 0, function () {
        var _i, _a, _b, columnIndexStr, widthInPixels, columnIndex, widthInPoints, column;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    console.log("  \uD83D\uDCCF Ustawianie szeroko\u015Bci ".concat(Object.keys(columnWidths).length, " kolumn..."));
                    for (_i = 0, _a = Object.entries(columnWidths); _i < _a.length; _i++) {
                        _b = _a[_i], columnIndexStr = _b[0], widthInPixels = _b[1];
                        columnIndex = parseInt(columnIndexStr);
                        widthInPoints = widthInPixels * 0.75;
                        column = sheet.getRange("".concat(columnIndex, ":").concat(columnIndex));
                        column.format.columnWidth = widthInPoints;
                    }
                    return [4 /*yield*/, context.sync()];
                case 1:
                    _c.sent();
                    console.log("  \u2705 Zastosowano szeroko\u015Bci kolumn (piksele \u2192 punkty)");
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Ustawia wysokości wierszy
 * API zwraca wysokości w PIKSELACH (dla DPI 96)
 * Excel API wymaga jednostek PUNKTÓW (points)
 */
function applyRowHeights(context, sheet, rowHeights) {
    return __awaiter(this, void 0, void 0, function () {
        var _i, _a, _b, rowIndexStr, heightInPixels, rowIndex, heightInPoints, row;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    console.log("  \uD83D\uDCCF Ustawianie wysoko\u015Bci ".concat(Object.keys(rowHeights).length, " wierszy..."));
                    for (_i = 0, _a = Object.entries(rowHeights); _i < _a.length; _i++) {
                        _b = _a[_i], rowIndexStr = _b[0], heightInPixels = _b[1];
                        rowIndex = parseInt(rowIndexStr);
                        heightInPoints = heightInPixels * 0.75;
                        row = sheet.getRange("".concat(rowIndex, ":").concat(rowIndex));
                        row.format.rowHeight = heightInPoints;
                    }
                    return [4 /*yield*/, context.sync()];
                case 1:
                    _c.sent();
                    console.log("  \u2705 Zastosowano wysoko\u015Bci wierszy (piksele \u2192 punkty)");
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Ustawia kolory tła dla całych wierszy
 */
function applyRowBackgroundColors(context, sheet, rowBackgroundColors) {
    return __awaiter(this, void 0, void 0, function () {
        var _i, _a, _b, rowIndexStr, color, rowIndex, row, excelColor;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    console.log("  \uD83C\uDFA8 Ustawianie kolor\u00F3w t\u0142a dla ".concat(Object.keys(rowBackgroundColors).length, " wierszy..."));
                    for (_i = 0, _a = Object.entries(rowBackgroundColors); _i < _a.length; _i++) {
                        _b = _a[_i], rowIndexStr = _b[0], color = _b[1];
                        rowIndex = parseInt(rowIndexStr);
                        row = sheet.getRange("".concat(rowIndex, ":").concat(rowIndex));
                        excelColor = hexToExcelColor(color);
                        if (excelColor) {
                            row.format.fill.color = excelColor;
                        }
                    }
                    return [4 /*yield*/, context.sync()];
                case 1:
                    _c.sent();
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * ═══════════════════════════════════════════════════════════
 * KROK 4: APLIKACJA KOMÓREK
 * ═══════════════════════════════════════════════════════════
 */
/**
 * Aplikuje komórki - wartości i formatowanie
 */
function applyCells(context, sheet, cellsData) {
    return __awaiter(this, void 0, void 0, function () {
        var compatibility, processedCount, _i, _a, cell;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    console.log("\uD83D\uDCDD Wstawianie ".concat(cellsData.items.length, " kom\u00F3rek..."));
                    compatibility = detectPlatformCompatibility();
                    console.log("\uD83D\uDD27 Kompatybilno\u015B\u0107 platformy:", {
                        platform: compatibility.platform,
                        version: compatibility.version,
                        isWindows10: compatibility.isWindows10,
                        supportsRichText: compatibility.supportsRichText,
                        maxCellsPerBatch: compatibility.maxCellsPerBatch
                    });
                    processedCount = 0;
                    _i = 0, _a = cellsData.items;
                    _b.label = 1;
                case 1:
                    if (!(_i < _a.length)) return [3 /*break*/, 5];
                    cell = _a[_i];
                    return [4 /*yield*/, applyCell(sheet, cell, compatibility)];
                case 2:
                    _b.sent();
                    processedCount++;
                    if (!(processedCount % compatibility.maxCellsPerBatch === 0)) return [3 /*break*/, 4];
                    return [4 /*yield*/, context.sync()];
                case 3:
                    _b.sent();
                    console.log("  \u23F3 Przetworzono ".concat(processedCount, "/").concat(cellsData.items.length, " kom\u00F3rek..."));
                    _b.label = 4;
                case 4:
                    _i++;
                    return [3 /*break*/, 1];
                case 5: return [4 /*yield*/, context.sync()];
                case 6:
                    _b.sent();
                    console.log("\u2705 Wstawiono ".concat(cellsData.items.length, " kom\u00F3rek"));
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Windows 10 Ultra Fallback - każda komórka w osobnym Excel.run() - najstarsza możliwa kompatybilność
 */
function applyCellsWindows10Fallback(context, sheet, cellsData) {
    return __awaiter(this, void 0, void 0, function () {
        var processedCount, successCount, _loop_1, _i, _a, cell;
        var _this = this;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    console.log("\uD83C\uDD98 Windows 10 Ultra Fallback: Wstawianie ".concat(cellsData.items.length, " kom\u00F3rek pojedynczo..."));
                    processedCount = 0;
                    successCount = 0;
                    _loop_1 = function (cell) {
                        var cellError_1;
                        return __generator(this, function (_c) {
                            switch (_c.label) {
                                case 0:
                                    _c.trys.push([0, 2, , 3]);
                                    return [4 /*yield*/, Excel.run(function (isolatedContext) { return __awaiter(_this, void 0, void 0, function () {
                                            var isolatedSheet, range, safeValue;
                                            var _a;
                                            return __generator(this, function (_b) {
                                                switch (_b.label) {
                                                    case 0:
                                                        isolatedSheet = isolatedContext.workbook.worksheets.getActiveWorksheet();
                                                        range = isolatedSheet.getRange(cell.address);
                                                        // Tylko wartość - żadnego formatowania
                                                        if (cell.value !== null && cell.value !== undefined) {
                                                            safeValue = String(cell.value);
                                                            range.values = [[safeValue]];
                                                        }
                                                        else if ((_a = cell.richText) === null || _a === void 0 ? void 0 : _a.fullText) {
                                                            range.values = [[String(cell.richText.fullText)]];
                                                        }
                                                        else {
                                                            range.values = [[""]];
                                                        }
                                                        return [4 /*yield*/, isolatedContext.sync()];
                                                    case 1:
                                                        _b.sent();
                                                        successCount++;
                                                        return [2 /*return*/];
                                                }
                                            });
                                        }); })];
                                case 1:
                                    _c.sent();
                                    return [3 /*break*/, 3];
                                case 2:
                                    cellError_1 = _c.sent();
                                    console.warn("\u26A0\uFE0F Windows 10 Ultra: Pomini\u0119to ".concat(cell.address, ":"), cellError_1);
                                    return [3 /*break*/, 3];
                                case 3:
                                    processedCount++;
                                    // Log co 25 komórek
                                    if (processedCount % 25 === 0) {
                                        console.log("  \u23F3 Windows 10 Ultra: ".concat(successCount, "/").concat(processedCount, "/").concat(cellsData.items.length, "..."));
                                    }
                                    return [2 /*return*/];
                            }
                        });
                    };
                    _i = 0, _a = cellsData.items;
                    _b.label = 1;
                case 1:
                    if (!(_i < _a.length)) return [3 /*break*/, 4];
                    cell = _a[_i];
                    return [5 /*yield**/, _loop_1(cell)];
                case 2:
                    _b.sent();
                    _b.label = 3;
                case 3:
                    _i++;
                    return [3 /*break*/, 1];
                case 4:
                    console.log("\u2705 Windows 10 Ultra: Zako\u0144czono ".concat(successCount, "/").concat(processedCount, " kom\u00F3rek"));
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Aplikuje formatowanie do komórki
 */
function applyCellFormat(range, format) {
    return __awaiter(this, void 0, void 0, function () {
        var bgColor;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!format.font) return [3 /*break*/, 2];
                    return [4 /*yield*/, applyFontFormat(range, format.font)];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2:
                    // Format liczb
                    if (format.numberFormat) {
                        range.numberFormat = [[format.numberFormat]];
                    }
                    // Kolor tła (jeśli nie jest ustawiony na poziomie wiersza)
                    if (format.backgroundColor) {
                        bgColor = hexToExcelColor(format.backgroundColor);
                        if (bgColor) {
                            range.format.fill.color = bgColor;
                        }
                    }
                    // Wyrównanie poziome
                    if (format.horizontalAlignment) {
                        range.format.horizontalAlignment = mapHorizontalAlignment(format.horizontalAlignment);
                    }
                    // Wyrównanie pionowe
                    if (format.verticalAlignment) {
                        range.format.verticalAlignment = mapVerticalAlignment(format.verticalAlignment);
                    }
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Aplikuje formatowanie do komórki BEZ czcionki (używane dla Rich Text)
 * Rich Text ma własne formatowanie czcionki, ale potrzebuje tła, wyrównania itp.
 */
function applyCellFormatWithoutFont(range, format) {
    return __awaiter(this, void 0, void 0, function () {
        var bgColor;
        return __generator(this, function (_a) {
            // Format liczb
            if (format.numberFormat) {
                range.numberFormat = [[format.numberFormat]];
            }
            // Kolor tła (jeśli nie jest ustawiony na poziomie wiersza)
            if (format.backgroundColor) {
                bgColor = hexToExcelColor(format.backgroundColor);
                if (bgColor) {
                    range.format.fill.color = bgColor;
                }
            }
            // Wyrównanie poziome
            if (format.horizontalAlignment) {
                range.format.horizontalAlignment = mapHorizontalAlignment(format.horizontalAlignment);
            }
            // Wyrównanie pionowe
            if (format.verticalAlignment) {
                range.format.verticalAlignment = mapVerticalAlignment(format.verticalAlignment);
            }
            return [2 /*return*/];
        });
    });
}
/**
 * Aplikuje pojedynczą komórkę z uwzględnieniem kompatybilności platformy
 */
function applyCell(sheet, cell, compatibility) {
    return __awaiter(this, void 0, void 0, function () {
        var range, firstRunFont, baseFont, fontToApply, baseFont;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    range = sheet.getRange(cell.address);
                    if (!(cell.richText && cell.richText.formattingRuns && cell.richText.formattingRuns.length > 0)) return [3 /*break*/, 9];
                    if (!(compatibility.isWindows10 || !compatibility.supportsRichText)) return [3 /*break*/, 5];
                    console.log("  \u26A0\uFE0F Windows 10 wykryte - pomijam Rich Text API dla \"".concat(cell.richText.fullText.substring(0, 20), "...\""));
                    // Fallback: użyj prostej wartości tekstowej + pierwsze dostępne formatowanie
                    range.values = [[cell.richText.fullText]];
                    firstRunFont = (_a = cell.richText.formattingRuns[0]) === null || _a === void 0 ? void 0 : _a.font;
                    baseFont = (_b = cell.format) === null || _b === void 0 ? void 0 : _b.font;
                    if (!(firstRunFont || baseFont)) return [3 /*break*/, 2];
                    fontToApply = firstRunFont || baseFont;
                    return [4 /*yield*/, applyFontFormat(range, fontToApply)];
                case 1:
                    _d.sent();
                    _d.label = 2;
                case 2:
                    if (!cell.format) return [3 /*break*/, 4];
                    return [4 /*yield*/, applyCellFormatWithoutFont(range, cell.format)];
                case 3:
                    _d.sent();
                    _d.label = 4;
                case 4: return [3 /*break*/, 8];
                case 5:
                    baseFont = ((_c = cell.format) === null || _c === void 0 ? void 0 : _c.font) || null;
                    return [4 /*yield*/, applyRichText(range, cell.richText, baseFont, compatibility)];
                case 6:
                    _d.sent();
                    if (!cell.format) return [3 /*break*/, 8];
                    return [4 /*yield*/, applyCellFormatWithoutFont(range, cell.format)];
                case 7:
                    _d.sent();
                    _d.label = 8;
                case 8: return [3 /*break*/, 11];
                case 9:
                    // Wartość - tylko jeśli nie ma Rich Text
                    if (cell.value !== null && cell.value !== undefined) {
                        range.values = [[cell.value]];
                    }
                    if (!cell.format) return [3 /*break*/, 11];
                    return [4 /*yield*/, applyCellFormat(range, cell.format)];
                case 10:
                    _d.sent();
                    _d.label = 11;
                case 11: return [2 /*return*/];
            }
        });
    });
}
/**
 * Aplikuje formatowanie czcionki
 */
function applyFontFormat(range, font) {
    return __awaiter(this, void 0, void 0, function () {
        var color;
        return __generator(this, function (_a) {
            if (font.name) {
                range.format.font.name = font.name;
            }
            if (font.size) {
                range.format.font.size = font.size;
            }
            if (font.bold !== undefined) {
                range.format.font.bold = font.bold;
            }
            if (font.italic !== undefined) {
                range.format.font.italic = font.italic;
            }
            if (font.underline !== undefined) {
                range.format.font.underline = font.underline ?
                    Excel.RangeUnderlineStyle.single :
                    Excel.RangeUnderlineStyle.none;
            }
            if (font.color) {
                color = hexToExcelColor(font.color);
                if (color) {
                    range.format.font.color = color;
                }
            }
            return [2 /*return*/];
        });
    });
}
/**
 * Aplikuje Rich Text - formatowanie wewnątrz komórki
 * Różne fragmenty tekstu mogą mieć różne style
 *
 * Używa Excel.CellValueType.formattedNumber/string z formatowaniem
 * Requirement Set: ExcelApi 1.16+ (Excel 2016+)
 *
 * @param range - Zakres komórki do sformatowania
 * @param richText - Dane Rich Text z fragmentami formatowania
 * @param baseFont - Bazowe formatowanie czcionki (z cell.format.font) używane w fallbacku gdy Rich Text API nie działa
 * @param compatibility - Informacje o kompatybilności platformy
 */
function applyRichText(range_1, richText_1) {
    return __awaiter(this, arguments, void 0, function (range, richText, baseFont, compatibility) {
        var cell, runs, richTextError_1, longestRun, _i, _a, run, _b, _c, run;
        if (baseFont === void 0) { baseFont = null; }
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _d.trys.push([0, 1, , 6]);
                    console.log("  \uD83C\uDFA8 Aplikowanie Rich Text: \"".concat(richText.fullText.substring(0, 30), "...\" (").concat(richText.formattingRuns.length, " fragment\u00F3w)"));
                    cell = range.getCell(0, 0);
                    // Sprawdź czy platforma wspiera Rich Text API
                    if (!compatibility.supportsRichText) {
                        console.log("  \u26A0\uFE0F Platforma nie wspiera Rich Text API - u\u017Cywam fallback");
                        throw new Error("Rich Text not supported on this platform");
                    }
                    // Próba 1: Użyj RichText przez setRichTextValue (jeśli dostępne)
                    if (typeof cell.setRichTextValue === 'function') {
                        try {
                            runs = richText.formattingRuns.map(function (run) {
                                var fontFormat = {};
                                if (run.font.name)
                                    fontFormat.name = run.font.name;
                                if (run.font.size)
                                    fontFormat.size = run.font.size;
                                if (run.font.bold !== undefined)
                                    fontFormat.bold = run.font.bold;
                                if (run.font.italic !== undefined)
                                    fontFormat.italic = run.font.italic;
                                if (run.font.underline !== undefined) {
                                    fontFormat.underline = run.font.underline ? "Single" : "None";
                                }
                                if (run.font.color) {
                                    var color = hexToExcelColor(run.font.color);
                                    if (color)
                                        fontFormat.color = color;
                                }
                                return {
                                    text: run.text,
                                    format: { font: fontFormat }
                                };
                            });
                            cell.setRichTextValue(runs);
                            console.log("  \u2705 Rich Text zaaplikowany przez setRichTextValue API");
                            return [2 /*return*/];
                        }
                        catch (richTextError) {
                            console.warn("  \u26A0\uFE0F setRichTextValue nie zadzia\u0142a\u0142o:", richTextError);
                        }
                    }
                    // Próba 2: Cell Value API - POMINIĘTA (nie wspiera richTextRuns w tej wersji)
                    // Excel JavaScript API nie wspiera richTextRuns w valuesAsJson w wersji 16.01
                    // Przejdź bezpośrednio do fallback
                    throw new Error("Rich Text API unavailable");
                case 1:
                    richTextError_1 = _d.sent();
                    // Fallback: Tekst prosty + formatowanie bazowe
                    console.log("  \u26A0\uFE0F Rich Text API niedost\u0119pne - u\u017Cywam fallback (formatowanie bazowe)");
                    range.values = [[richText.fullText]];
                    if (!baseFont) return [3 /*break*/, 3];
                    console.log("  \uD83D\uDD04 Fallback: u\u017Cywam bazowego formatowania czcionki (bold: ".concat(baseFont.bold, ")"));
                    return [4 /*yield*/, applyFontFormat(range, baseFont)];
                case 2:
                    _d.sent();
                    return [3 /*break*/, 5];
                case 3:
                    longestRun = richText.formattingRuns[0];
                    for (_i = 0, _a = richText.formattingRuns; _i < _a.length; _i++) {
                        run = _a[_i];
                        if (run.text.length > longestRun.text.length) {
                            longestRun = run;
                        }
                    }
                    console.log("  \uD83D\uDD04 Fallback: u\u017Cywam formatowania najd\u0142u\u017Cszego fragmentu \"".concat(longestRun.text, "\" (bold: ").concat(longestRun.font.bold, ")"));
                    return [4 /*yield*/, applyFontFormat(range, longestRun.font)];
                case 4:
                    _d.sent();
                    _d.label = 5;
                case 5:
                    // Log wszystkich fragmentów dla debugowania
                    console.log("  \uD83D\uDCDD Fragmenty Rich Text (fallback - jedno formatowanie):");
                    for (_b = 0, _c = richText.formattingRuns; _b < _c.length; _b++) {
                        run = _c[_b];
                        console.log("    - [".concat(run.startIndex, "+").concat(run.length, "] \"").concat(run.text, "\" \u2192 bold:").concat(run.font.bold, ", color:").concat(run.font.color));
                    }
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    });
}
/**
 * ═══════════════════════════════════════════════════════════
 * KROK 5: SCALANIE KOMÓREK
 * ═══════════════════════════════════════════════════════════
 */
/**
 * Scala komórki według podanych zakresów
 */
function applyMergedCells(context, sheet, mergedCells) {
    return __awaiter(this, void 0, void 0, function () {
        var _i, _a, rangeAddress, range;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (mergedCells.ranges.length === 0) {
                        return [2 /*return*/];
                    }
                    console.log("\uD83D\uDD17 Scalanie ".concat(mergedCells.ranges.length, " zakres\u00F3w..."));
                    for (_i = 0, _a = mergedCells.ranges; _i < _a.length; _i++) {
                        rangeAddress = _a[_i];
                        try {
                            range = sheet.getRange(rangeAddress);
                            range.merge(false);
                        }
                        catch (error) {
                            console.warn("  \u26A0\uFE0F B\u0142\u0105d przy scalaniu ".concat(rangeAddress, ":"), error);
                        }
                    }
                    return [4 /*yield*/, context.sync()];
                case 1:
                    _b.sent();
                    console.log('✅ Komórki scalone');
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * ═══════════════════════════════════════════════════════════
 * FUNKCJE POMOCNICZE
 * ═══════════════════════════════════════════════════════════
 */
/**
 * Konwertuje kolor HEX (AARRGGBB) na format Excel (#RRGGBB)
 */
function hexToExcelColor(hex) {
    if (!hex || hex.trim().length === 0) {
        return null;
    }
    hex = hex.replace('#', '').trim();
    if (hex.length !== 6 && hex.length !== 8) {
        return null;
    }
    // AARRGGBB → RRGGBB
    if (hex.length === 8) {
        hex = hex.substring(2);
    }
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        return null;
    }
    return "#".concat(hex.toUpperCase());
}
/**
 * Mapuje wyrównanie poziome
 */
function mapHorizontalAlignment(alignment) {
    var map = {
        'General': Excel.HorizontalAlignment.general,
        'Left': Excel.HorizontalAlignment.left,
        'Center': Excel.HorizontalAlignment.center,
        'Right': Excel.HorizontalAlignment.right,
        'Justify': Excel.HorizontalAlignment.justify,
        'Distributed': Excel.HorizontalAlignment.distributed,
        'Fill': Excel.HorizontalAlignment.fill
    };
    return map[alignment] || Excel.HorizontalAlignment.general;
}
/**
 * Mapuje wyrównanie pionowe
 */
function mapVerticalAlignment(alignment) {
    var map = {
        'Top': Excel.VerticalAlignment.top,
        'Center': Excel.VerticalAlignment.center,
        'Bottom': Excel.VerticalAlignment.bottom,
        'Justify': Excel.VerticalAlignment.justify,
        'Distributed': Excel.VerticalAlignment.distributed
    };
    return map[alignment] || Excel.VerticalAlignment.bottom;
}
//# sourceMappingURL=TemplateLoaderWrapper.js.map